/**
 * Widget Settings Context
 * Provides global state for widget configurations (enabled, size, position, pinned, settings)
 */

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef
} from 'react'
import type {
  WidgetConfig,
  WidgetSize,
  WidgetSettings,
  WidgetDesktopPosition
} from '@/types/widget'
import { DEFAULT_WIDGET_CONFIGS } from '@/data/widgets'
import { WidgetSettingsService } from '@/services/widget-settings-service'
import { logInfo, logError } from '@/lib/logger'

interface EnableWidgetOptions {
  sidebarIndex?: number
  pinnedToDesktop?: boolean
  desktopPosition?: WidgetDesktopPosition
}

type SidebarTransform = (
  sidebar: WidgetConfig[],
  configs: WidgetConfig[]
) => WidgetConfig[]

const hasValidId = (config: WidgetConfig) => Boolean(config.id && config.id.trim() !== '')

const isSidebarWidget = (config: WidgetConfig) =>
  hasValidId(config) && config.enabled && !config.pinnedToDesktop

const clampIndex = (value: number | undefined, length: number) => {
  if (length <= 0) return 0
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return Math.max(0, length)
  }
  return Math.min(Math.max(Math.floor(value), 0), length)
}

const buildSidebarList = (configs: WidgetConfig[]) =>
  configs.filter(isSidebarWidget).sort((a, b) => a.position - b.position)

const insertSidebarWidget = (
  sidebar: WidgetConfig[],
  configs: WidgetConfig[],
  widgetId: string,
  targetIndex?: number
) => {
  const filtered = sidebar.filter((widget) => widget.id !== widgetId)
  const widget = configs.find((config) => config.id === widgetId && isSidebarWidget(config))
  if (!widget) {
    return filtered
  }
  const insertionIndex = clampIndex(targetIndex ?? filtered.length, filtered.length)
  filtered.splice(insertionIndex, 0, widget)
  return filtered
}

const applySidebarOrdering = (
  configs: WidgetConfig[],
  transform?: SidebarTransform
): WidgetConfig[] => {
  const baseSidebar = buildSidebarList(configs)
  const workingSidebar = transform ? transform([...baseSidebar], configs) : [...baseSidebar]
  if (transform) {
    const seen = new Set(workingSidebar.map((widget) => widget.id))
    baseSidebar.forEach((widget) => {
      if (!seen.has(widget.id)) {
        workingSidebar.push(widget)
        seen.add(widget.id)
      }
    })
  }
  const positionMap = new Map(workingSidebar.map((widget, index) => [widget.id, index]))
  return configs.map((config) => {
    const nextPosition = positionMap.get(config.id)
    if (nextPosition === undefined || config.position === nextPosition) {
      return config
    }
    return { ...config, position: nextPosition }
  })
}

interface WidgetSettingsContextType {
  // State
  widgetConfigs: WidgetConfig[]
  isLoading: boolean

  // Actions
  toggleWidget: (widgetId: string) => void
  updateWidgetSize: (widgetId: string, size: WidgetSize) => void
  updateWidgetPosition: (widgetId: string, newPosition: number) => void
  pinWidget: (widgetId: string, pinned: boolean) => void
  updateWidgetSettings: (widgetId: string, settings: WidgetSettings) => void
  reorderWidgets: (newOrder: WidgetConfig[]) => void
  resetToDefaults: () => void
  reloadWidgetSettings: () => Promise<void>
  enableWidget: (widgetId: string, options?: EnableWidgetOptions) => void
  disableWidget: (widgetId: string) => void
  moveWidgetWithinSidebar: (widgetId: string, targetIndex: number) => void
  moveWidgetToDesktop: (widgetId: string, position: WidgetDesktopPosition) => void
  moveWidgetToSidebar: (widgetId: string, targetIndex?: number) => void

  // Desktop Widget Actions
  pinWidgetToDesktop: (widgetId: string, position: WidgetDesktopPosition) => void
  unpinWidgetFromDesktop: (widgetId: string) => void
  updateWidgetDesktopPosition: (widgetId: string, position: WidgetDesktopPosition) => void
  moveWidgetFromDesktopToSidebar: (widgetId: string, targetIndex?: number) => void
}

const WidgetSettingsContext = createContext<WidgetSettingsContextType | null>(null)

export function WidgetSettingsProvider({ children }: { children: ReactNode }) {
  const [widgetConfigs, setWidgetConfigs] = useState<WidgetConfig[]>(DEFAULT_WIDGET_CONFIGS)
  const [isLoading, setIsLoading] = useState(true)
  // Gespeichert wird erst nach einem erfolgreichen Load. Nach einem Fehler
  // stehen nur Standard-Widgets im State; die zu speichern hieße, die echten
  // Widgets des Nutzers zu überschreiben (#908).
  const canPersistRef = useRef(false)

  // Lädt Widget-Konfigurationen beim Start
  useEffect(() => {
    loadWidgetSettings()
  }, [])

  // Widget settings load on mount and on explicit login flows (see auth-sync-service).

  /**
   * Lädt Widget-Konfigurationen aus Backend
   */
  const loadWidgetSettings = async () => {
    setIsLoading(true)
    try {
      const configs = await WidgetSettingsService.loadUserWidgetSettings()
      // Filter out widgets with empty IDs
      const validConfigs = configs.filter(c => c.id && c.id.trim() !== '')
      setWidgetConfigs(validConfigs)
      canPersistRef.current = true
      logInfo(
        { context: 'WidgetSettingsContext', count: validConfigs.length },
        'Widget-Konfigurationen geladen'
      )
    } catch (error) {
      logError(error as Error, {
        context: 'Widget Settings Context: loadWidgetSettings',
        fallback: 'Standard-Widgets angezeigt, Speichern gesperrt',
      })
      setWidgetConfigs(DEFAULT_WIDGET_CONFIGS)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Speichert Widget-Konfigurationen
   */
  const saveWidgetSettings = useCallback(
    async (configs: WidgetConfig[]) => {
      if (!canPersistRef.current) {
        logError(new Error('Widget-Konfigurationen nicht geladen, Speichern verworfen'), {
          context: 'Widget Settings Context: saveWidgetSettings',
          count: configs.length,
        })
        return
      }
      try {
        await WidgetSettingsService.saveWidgetSettings(configs)

        // Broadcast event für andere Komponenten
        window.dispatchEvent(
          new CustomEvent('widget-settings-changed', {
            detail: { configs }
          })
        )
      } catch (error) {
        logError(error as Error, { context: 'Widget Settings Context: saveWidgetSettings' })
      }
    },
    []
  )

  /**
   * Aktiviert/Deaktiviert ein Widget
   */
  const toggleWidget = useCallback(
    (widgetId: string) => {
      setWidgetConfigs((prev) => {
        const updated = prev.map((config) =>
          config.id === widgetId ? { ...config, enabled: !config.enabled } : config
        )
        saveWidgetSettings(updated)
        return updated
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Ändert die Größe eines Widgets
   */
  const updateWidgetSize = useCallback(
    (widgetId: string, size: WidgetSize) => {
      setWidgetConfigs((prev) => {
        const updated = prev.map((config) =>
          config.id === widgetId ? { ...config, size } : config
        )
        saveWidgetSettings(updated)
        return updated
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Ändert die Position eines Widgets
   */
  const updateWidgetPosition = useCallback(
    (widgetId: string, newPosition: number) => {
      setWidgetConfigs((prev) => {
        const updated = prev.map((config) =>
          config.id === widgetId ? { ...config, position: newPosition } : config
        )
        saveWidgetSettings(updated)
        return updated
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Pinnt/Entpinnt ein Widget
   */
  const pinWidget = useCallback(
    (widgetId: string, pinned: boolean) => {
      setWidgetConfigs((prev) => {
        const updated = prev.map((config) =>
          config.id === widgetId ? { ...config, pinned } : config
        )
        saveWidgetSettings(updated)
        return updated
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Aktualisiert widget-spezifische Einstellungen
   */
  const updateWidgetSettings = useCallback(
    (widgetId: string, settings: WidgetSettings) => {
      setWidgetConfigs((prev) => {
        const updated = prev.map((config) =>
          config.id === widgetId ? { ...config, settings: { ...config.settings, ...settings } } : config
        )
        saveWidgetSettings(updated)
        return updated
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Sortiert Widgets neu (für Drag-and-Drop)
   */
  const reorderWidgets = useCallback(
    (newOrder: WidgetConfig[]) => {
      // Aktualisiere Positionen basierend auf Array-Index
      const updatedOrder = newOrder.map((config, index) => ({
        ...config,
        position: index
      }))
      setWidgetConfigs(updatedOrder)
      saveWidgetSettings(updatedOrder)
    },
    [saveWidgetSettings]
  )

  /**
   * Setzt alle Widget-Konfigurationen auf Defaults zurück
   */
  const resetToDefaults = useCallback(() => {
    setWidgetConfigs(DEFAULT_WIDGET_CONFIGS)
    saveWidgetSettings(DEFAULT_WIDGET_CONFIGS)
    logInfo('Widget-Konfigurationen auf Defaults zurückgesetzt')
  }, [saveWidgetSettings])

  /**
   * Lädt Widget-Konfigurationen neu (z.B. nach Login)
   */
  const reloadWidgetSettings = useCallback(async () => {
    await loadWidgetSettings()
  }, [])

  /**
   * Aktiviert ein Widget und platziert es in der Sidebar oder auf dem Desktop
   */
  const enableWidget = useCallback(
    (widgetId: string, options: EnableWidgetOptions = {}) => {
      setWidgetConfigs((prev) => {
        let found = false
        const shouldPinToDesktop = Boolean(options.pinnedToDesktop)
        const updated = prev.map((config) => {
          if (config.id !== widgetId) return config
          found = true
          return {
            ...config,
            enabled: true,
            pinnedToDesktop: shouldPinToDesktop,
            desktopPosition: shouldPinToDesktop
              ? options.desktopPosition ?? config.desktopPosition
              : undefined
          }
        })

        if (!found) return prev

        const withSidebarOrder = applySidebarOrdering(
          updated,
          (sidebar) => {
            if (shouldPinToDesktop) {
              return sidebar.filter((widget) => widget.id !== widgetId)
            }
            return insertSidebarWidget(sidebar, updated, widgetId, options.sidebarIndex)
          }
        )

        saveWidgetSettings(withSidebarOrder)
        return withSidebarOrder
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Deaktiviert ein Widget komplett
   */
  const disableWidget = useCallback(
    (widgetId: string) => {
      setWidgetConfigs((prev) => {
        let found = false
        const updated = prev.map((config) => {
          if (config.id !== widgetId) return config
          found = true
          return {
            ...config,
            enabled: false,
            pinnedToDesktop: false,
            desktopPosition: undefined
          }
        })

        if (!found) return prev

        const withSidebarOrder = applySidebarOrdering(updated, (sidebar) =>
          sidebar.filter((widget) => widget.id !== widgetId)
        )

        saveWidgetSettings(withSidebarOrder)
        return withSidebarOrder
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Verschiebt ein Widget innerhalb der Sidebar
   */
  const moveWidgetWithinSidebar = useCallback(
    (widgetId: string, targetIndex: number) => {
      setWidgetConfigs((prev) => {
        const sidebarWidgets = buildSidebarList(prev)
        const isInSidebar = sidebarWidgets.some((widget) => widget.id === widgetId)
        if (!isInSidebar) {
          return prev
        }

        const reordered = applySidebarOrdering(prev, (sidebar) =>
          insertSidebarWidget(sidebar, prev, widgetId, targetIndex)
        )
        saveWidgetSettings(reordered)
        return reordered
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Verschiebt/aktiviert ein Widget direkt auf dem Desktop
   */
  const moveWidgetToDesktop = useCallback(
    (widgetId: string, position: WidgetDesktopPosition) => {
      setWidgetConfigs((prev) => {
        let found = false
        const updated = prev.map((config) => {
          if (config.id !== widgetId) return config
          found = true
          return {
            ...config,
            enabled: true,
            pinnedToDesktop: true,
            desktopPosition: position
          }
        })

        if (!found) {
          return prev
        }

        const withSidebarOrder = applySidebarOrdering(updated, (sidebar) =>
          sidebar.filter((widget) => widget.id !== widgetId)
        )

        saveWidgetSettings(withSidebarOrder)
        return withSidebarOrder
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Verschiebt ein Widget (zurueck) in die Sidebar
   */
  const moveWidgetToSidebar = useCallback(
    (widgetId: string, targetIndex?: number) => {
      setWidgetConfigs((prev) => {
        let found = false
        const updated = prev.map((config) => {
          if (config.id !== widgetId) return config
          found = true
          return {
            ...config,
            enabled: true,
            pinnedToDesktop: false,
            desktopPosition: undefined
          }
        })

        if (!found) return prev

        const withSidebarOrder = applySidebarOrdering(
          updated,
          (sidebar) => insertSidebarWidget(sidebar, updated, widgetId, targetIndex)
        )

        saveWidgetSettings(withSidebarOrder)
        return withSidebarOrder
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Pinnt ein Widget auf den Desktop
   */
  const pinWidgetToDesktop = useCallback(
    (widgetId: string, position: WidgetDesktopPosition) => {
      setWidgetConfigs((prev) => {
        const updated = prev.map((config) =>
          config.id === widgetId
            ? { ...config, pinnedToDesktop: true, desktopPosition: position }
            : config
        )
        saveWidgetSettings(updated)
        return updated
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Entfernt ein Widget vom Desktop
   */
  const unpinWidgetFromDesktop = useCallback(
    (widgetId: string) => {
      setWidgetConfigs((prev) => {
        const updated = prev.map((config) =>
          config.id === widgetId
            ? { ...config, pinnedToDesktop: false, desktopPosition: undefined }
            : config
        )
        saveWidgetSettings(updated)
        return updated
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Aktualisiert die Desktop-Position eines Widgets
   */
  const updateWidgetDesktopPosition = useCallback(
    (widgetId: string, position: WidgetDesktopPosition) => {
      setWidgetConfigs((prev) => {
        const updated = prev.map((config) =>
          config.id === widgetId
            ? { ...config, desktopPosition: position }
            : config
        )
        saveWidgetSettings(updated)
        return updated
      })
    },
    [saveWidgetSettings]
  )

  /**
   * Verschiebt ein Desktop-Widget zurǬck in die Sidebar und platziert es an einer bestimmten Position
   */
  const moveWidgetFromDesktopToSidebar = useCallback(
    (widgetId: string, targetIndex?: number) => {
      setWidgetConfigs((prev) => {
        const withUnpinned = prev.map((config) =>
          config.id === widgetId
            ? { ...config, pinnedToDesktop: false, desktopPosition: undefined }
            : config
        )

        const sidebarWidgets = withUnpinned
          .filter((config) => config.id && config.id.trim() !== '' && config.enabled && !config.pinnedToDesktop)
          .sort((a, b) => a.position - b.position)

        const currentIndex = sidebarWidgets.findIndex((config) => config.id === widgetId)
        if (currentIndex === -1) {
          saveWidgetSettings(withUnpinned)
          return withUnpinned
        }

        const [movingWidget] = sidebarWidgets.splice(currentIndex, 1)
        const insertionIndex = Math.min(
          Math.max(0, targetIndex ?? sidebarWidgets.length),
          sidebarWidgets.length
        )
        sidebarWidgets.splice(insertionIndex, 0, movingWidget)

        const positionMap = new Map(sidebarWidgets.map((config, index) => [config.id, index]))
        const finalConfigs = withUnpinned.map((config) => {
          const newPosition = positionMap.get(config.id)
          if (newPosition === undefined) return config
          return { ...config, position: newPosition }
        })

        saveWidgetSettings(finalConfigs)
        return finalConfigs
      })
    },
    [saveWidgetSettings]
  )

  return (
    <WidgetSettingsContext.Provider
      value={{
        widgetConfigs,
        isLoading,
        toggleWidget,
        updateWidgetSize,
        updateWidgetPosition,
        pinWidget,
        updateWidgetSettings,
        reorderWidgets,
        resetToDefaults,
        reloadWidgetSettings,
        enableWidget,
        disableWidget,
        moveWidgetWithinSidebar,
        moveWidgetToDesktop,
        moveWidgetToSidebar,
        pinWidgetToDesktop,
        unpinWidgetFromDesktop,
        updateWidgetDesktopPosition,
        moveWidgetFromDesktopToSidebar
      }}
    >
      {children}
    </WidgetSettingsContext.Provider>
  )
}

export function useWidgetSettings() {
  const context = useContext(WidgetSettingsContext)
  if (!context) {
    throw new Error('useWidgetSettings must be used within WidgetSettingsProvider')
  }
  return context
}
